/**
 * QuantityStepper — Blinkit-style ADD button that morphs into a "− qty +"
 * stepper after the first click.
 *
 * Pure presentational + a couple of optional callbacks; *does not* know about
 * cart APIs directly so it can be reused for wishlist, restaurant cart, etc.
 *
 * Styling lives in `QuantityStepper.css` (plain CSS — no Tailwind required,
 * which the CRA build does not actually compile in this project).
 *
 * Usage:
 *   <QuantityStepper
 *     value={qty}
 *     onChange={(next) => setQty(next)}
 *     min={0}
 *     max={row.stock_quantity}
 *     disabled={!inStock}
 *   />
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './QuantityStepper.css';

export interface QuantityStepperProps {
  value: number;
  onChange: (next: number) => void | Promise<void>;
  min?: number;
  max?: number;
  disabled?: boolean;
  loading?: boolean;
  /** Override label, defaults to ADD */
  addLabel?: string;
  /** Inline style overrides for the root (e.g. width) */
  style?: React.CSSProperties;
  className?: string;
}

export const QuantityStepper: React.FC<QuantityStepperProps> = ({
  value,
  onChange,
  min = 0,
  max = 99,
  disabled = false,
  loading = false,
  addLabel = 'ADD',
  style,
  className,
}) => {
  const inc = () => {
    if (disabled || loading) return;
    if (value >= max) return;
    onChange(value + 1);
  };
  const dec = () => {
    if (disabled || loading) return;
    if (value <= min) return;
    onChange(value - 1);
  };

  const empty = value <= 0;

  return (
    <div className={`qs2-root ${className ?? ''}`} style={style}>
      <AnimatePresence initial={false} mode="wait">
        {empty ? (
          <motion.button
            key="add"
            type="button"
            disabled={disabled || loading}
            onClick={inc}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.15 }}
            className="qs2-add"
          >
            {loading ? '...' : addLabel}
          </motion.button>
        ) : (
          <motion.div
            key="stepper"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.15 }}
            className="qs2-stepper"
          >
            <button
              type="button"
              onClick={dec}
              disabled={disabled || loading || value <= min}
              aria-label="Decrease quantity"
              className="qs2-btn"
            >
              −
            </button>
            <span aria-live="polite" className="qs2-value">
              {loading ? '...' : value}
            </span>
            <button
              type="button"
              onClick={inc}
              disabled={disabled || loading || value >= max}
              aria-label="Increase quantity"
              className="qs2-btn"
            >
              +
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuantityStepper;

